import { forwardRef, memo, useMemo, type ReactNode, type ReactElement } from 'react';
import size from 'lodash/size';
import isBoolean from 'lodash/isBoolean';
import { useIntl, FormattedMessage } from 'react-intl';
// Material UI imports
import type { SxProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import IconButton from '@mui/material/IconButton';
// Material Icon imports
import PushPin from '@mui/icons-material/PushPin';
import PushPinOutlined from '@mui/icons-material/PushPinOutlined';
// Skillmore UI Components
import { fontWeightMedium } from '@empathco/ui-components/src/styles/themeOptions';
import BoxTypography from '@empathco/ui-components/src/mixins/BoxTypography';
import GridBox from '@empathco/ui-components/src/mixins/GridBox';
import { getParagraph, getStandardLink } from '@empathco/ui-components/src/helpers/values';
import FetchFailedAlert from '@empathco/ui-components/src/elements/FetchFailedAlert';
import LoadingPlaceholder from '@empathco/ui-components/src/elements/LoadingPlaceholder';
import ExportButton, { ExportParams } from '@empathco/ui-components/src/elements/ExportButton';
import InfoButton from '@empathco/ui-components/src/elements/InfoButton';
// local imports
import { Skill } from '../models/skill';
import { Job } from '../models/job';
import {
  EmployeeCountByDate, MDTargetedJob, MDJobsSkillsGap,
  DAAverageSkills, DACloseMatchJobs, DAHiringTrends, DAJobsMovement, DAMostEffectiveCourses,
  DAMostEffectiveAdvisor, DASkillsInsights, DAEmployeeVelocity, DACurrentRoleMatch, DAJobMovementSkills,
  DASkillSupply, DASkillsDevelopment, DASkillsGrowth, DAJobRelatedSkills, DASkillCourseMapping, DAUpskillingVelocity,
  DAOrgUsage
} from '../graphql/types';
import { DashboardExportParams } from '../context/supervisor';
import TopChartSimple from './TopChartSimple';
import TopChartStacked from './TopChartStacked';
import TopChartBars from './TopChartBars';
import TopChartTimeline from './TopChartTimeline';
import TopChartVBars from './TopChartVBars';
import TopChartRadar from './TopChartRadar';
// SCSS imports
import { overlayDefault } from '@empathco/ui-components/src/styles/modules/Overlay.module.scss';
import {
  card, cardTitle, cardTitleWithoutExport, header, filtersContainer, content, emptyBox, emptyMsg
} from './TopChart.module.scss';

const iconBtnSx: SxProps<Theme> = { mx: -2 } as const;

// Manager Dashboard only
const TOP_CHART_IN_DEMAND = 'in_demand' as const;
type TOP_CHART_SKILLS = 'skills';
type TOP_CHART_JOBS = 'jobs';
const TOP_CHART_EMPLOYEE_SKILLS = 'employee_skills' as const; // + HRBP Dashboard: Skills
const TOP_CHART_SKILLS_GAP = 'skills_gap' as const;
// HRBP Dashboard: Skills
const TOP_CHART_SUPPLY_DEMAND = 'supply_demand' as const;
const TOP_CHART_SKILLS_GROWTH = 'skills_growth' as const;
// Skills Development chart:
const TOP_CHART_SKILLS_DEVELOPMENT = 'skills_development' as const; // without Advisors/Courses
const TOP_CHART_SKILL_ADVISORS = 'skill_advisors' as const; // Advisors variant
const TOP_CHART_SKILL_COURSES = 'skill_courses' as const; // Courses variant
const TOP_CHART_EFFECTIVE_COURSES = 'effective_courses' as const;
const TOP_CHART_COURSE_COMPLETION = 'course_completion' as const;
const TOP_CHART_EFFECTIVE_ADVISORS = 'effective_advisors' as const;
// Skill Course Mapping chart
const TOP_CHART_SKILL_COURSE_MAPPING = 'skill_course_mapping' as const;
  const TOP_CHART_COURSE_SKILL_MAPPING = 'course_skill_mapping' as const;
// HRBP Dashboard: Jobs
const TOP_CHART_FREQ_SKILL_GAP = 'frequent_skill_gap' as const;
const TOP_CHART_HIRING_TRENDS = 'hiring_trends' as const;
const TOP_CHART_JOBS_MOVEMENT = 'jobs_movement' as const;
const TOP_CHART_JOB_RELATED_SKILLS = 'job_skills' as const;
// HRBP Dashboard: Employees
const TOP_CHART_JOB_MOVEMENT_SKILLS = 'job_movement_skills' as const;
const TOP_CHART_JOBS_MATCH = 'close_match_jobs' as const;
const TOP_CHART_AVG_SKILLS = 'job_average_skills' as const;
const TOP_CHART_EMPLOYEE_VELOCITY = 'employee_velocity' as const;
const TOP_CHART_CURRENT_ROLE_MATCH = 'current_role_match' as const;
const TOP_CHART_UPSKILLING = 'upskilling_velocity' as const;
const TOP_CHART_ORG_USAGE = 'usage_by_org' as const;

export type TopChartVariant =
  // Manager Dashboard
  typeof TOP_CHART_IN_DEMAND | TOP_CHART_SKILLS | TOP_CHART_JOBS | typeof TOP_CHART_SKILLS_GAP |
  // Manager + (HRBP Dashboard: Skills)
  typeof TOP_CHART_EMPLOYEE_SKILLS |
  // HRBP Dashboard
  typeof TOP_CHART_JOBS_MATCH | typeof TOP_CHART_AVG_SKILLS | typeof TOP_CHART_SKILLS_GROWTH | typeof TOP_CHART_SUPPLY_DEMAND |
  typeof TOP_CHART_SKILL_ADVISORS | typeof TOP_CHART_SKILL_COURSES | typeof TOP_CHART_SKILLS_DEVELOPMENT |
  typeof TOP_CHART_EFFECTIVE_COURSES | typeof TOP_CHART_COURSE_COMPLETION | typeof TOP_CHART_EFFECTIVE_ADVISORS |
  typeof TOP_CHART_JOBS_MOVEMENT | typeof TOP_CHART_HIRING_TRENDS | typeof TOP_CHART_FREQ_SKILL_GAP |
  typeof TOP_CHART_EMPLOYEE_VELOCITY | typeof TOP_CHART_CURRENT_ROLE_MATCH | typeof TOP_CHART_UPSKILLING |
  typeof TOP_CHART_SKILL_COURSE_MAPPING | typeof TOP_CHART_COURSE_SKILL_MAPPING | typeof TOP_CHART_JOB_MOVEMENT_SKILLS |
  typeof TOP_CHART_JOB_RELATED_SKILLS | typeof TOP_CHART_ORG_USAGE;

export type TopChartDataType = (
  Skill | Job | EmployeeCountByDate | MDTargetedJob | MDJobsSkillsGap |
  DAMostEffectiveCourses | DAMostEffectiveAdvisor | DACloseMatchJobs | DAAverageSkills | DAJobsMovement |
  DASkillSupply | DASkillsDevelopment | DASkillsGrowth | DAUpskillingVelocity |
  DAEmployeeVelocity | DACurrentRoleMatch | DASkillCourseMapping | DAJobRelatedSkills | DASkillsInsights
)[];

export type TopChartLayout = 'normal' | 'wide' | 'full';

export type TopChartProps = {
  readonly variant: TopChartVariant;
  readonly layout?: TopChartLayout;
  readonly info?: string | null;
  readonly hrbp?: boolean;
  readonly data?: TopChartDataType | null;
  readonly totalEmployees?: number | null;
  readonly path?: string;
  readonly generating?: boolean | null;
  readonly pending?: boolean | null;
  readonly failed?: boolean | null;
  readonly failureMessage?: string;
  readonly action?: ReactNode;
  readonly filters?: ReactNode;
  readonly exportEndpoint?: string;
  readonly exportParams?: DashboardExportParams | null;
  readonly exportDisabled?: boolean | null;
  readonly exportPending?: boolean | null;
  readonly withInfoButton?: boolean | null;
  readonly optionalParams?: {
    years?: number | null;
    upwardJobMovement?: boolean | null;
    internalHiresOnly?: boolean | null;
    withoutInDemandSkills?: boolean | null;
  } | null;
  readonly pinned?: boolean;
  readonly onPin?: () => void;
  readonly pinning?: boolean;
}

const TopChart = forwardRef<HTMLDivElement, TopChartProps>(({
  variant,
  layout = 'normal',
  info,
  hrbp = false,
  data,
  totalEmployees,
  path,
  pending: parentPending = false,
  generating = false,
  failed = false,
  failureMessage,
  action,
  filters,
  exportEndpoint,
  exportParams,
  exportDisabled = false,
  exportPending = false,
  withInfoButton = false,
  optionalParams,
  pinned,
  onPin,
  pinning
  // eslint-disable-next-line complexity, max-lines-per-function
}, ref) => {
  const { formatMessage } = useIntl();
  const pending = parentPending || generating;

  const values = useMemo(() => ({
    link: getStandardLink({ href: formatMessage({ id: 'supervisor.dashboard.in_demand.link' }) }),
    line: getParagraph()
  }), [formatMessage]);

  const infoValues = useMemo(() => ({ indemand: !optionalParams?.withoutInDemandSkills }), [optionalParams]);

  const wide = layout !== 'normal';

  const PinIcon = isBoolean(pinned) ? (pinned && PushPin) || PushPinOutlined : undefined;

  const innerContent = (
    <>
      <CardHeader
          disableTypography
          avatar={PinIcon ? (
            <IconButton
                color="primary"
                aria-label={formatMessage({ id: pinned ? 'hr.dashboard.button.unpin' : 'hr.dashboard.button.pin' })}
                disabled={pinning || !onPin ? true : undefined}
                onClick={onPin}
                sx={iconBtnSx}
            >
              <PinIcon color="inherit"/>
            </IconButton>
          ) : undefined}
          title={
            <Box className={header}>
              <FormattedMessage
                  id={`supervisor.dashboard.${variant === TOP_CHART_SKILL_ADVISORS || variant === TOP_CHART_SKILL_COURSES
                    ? TOP_CHART_SKILLS_DEVELOPMENT : variant}`}
                  values={{
                    mode: hrbp ? 'hr' : 'manager',
                    indemand: !optionalParams?.withoutInDemandSkills
                  }}
              />
            </Box>
          }
          subheader={info && !failed && !pending && size(data) >= 1 ? (withInfoButton && (
            <InfoButton
                help={info}
                values={infoValues}
                placement="right"
                small
            />
          )) || (
            <BoxTypography
                variant="body2"
                color="text.label"
                fontStyle="italic"
                pt={0.25}
                px={2}
                display="flex"
            >
              <FormattedMessage id={info} defaultMessage={info}/>
            </BoxTypography>
          ) : undefined}
          action={(action && (
            <Box
                display="flex"
                alignItems="center"
                justifyContent="flex-end"
                flexWrap="wrap"
                pl={1}
            >
              {exportPending ? (
                <Box pr={1} color="action.disabled">
                  <CircularProgress size={22} color="inherit"/>
                </Box>
              ) : undefined}
              {size((action as ReactElement<{ children?: unknown[]; }>).props?.children) >= 2 ? action : (
                <Box py={1}>
                  {action}
                </Box>
              )}
              {/* TODO: ViewStyle switch */}
              {exportEndpoint ? (
                <Box pl={1} py={1}>
                  <ExportButton
                      endpoint={exportEndpoint}
                      disabled={exportDisabled || exportPending}
                      params={exportParams as ExportParams | null}
                  />
                </Box>
              ) : undefined}
            </Box>
          )) || (exportEndpoint && (
            <Box pl={1} py={1}>
              <ExportButton
                  endpoint={exportEndpoint}
                  pending={exportPending}
                  disabled={exportDisabled}
                  params={exportParams as ExportParams | null}
              />
            </Box>
          )) || undefined}
          className={action || exportEndpoint ? cardTitle : cardTitleWithoutExport}
      />
      {filters ? (
        <>
          <Divider/>
          <Box
              display="flex"
              flexDirection="column"
              pt={1}
              className={filtersContainer}
          >
            {filters}
          </Box>
        </>
      ) : undefined}
      {(failed && <FetchFailedAlert flat message={failureMessage}/>) ||
      ((pending && !data) && (
        <LoadingPlaceholder flat fullHeight message={generating ? 'hr.dashboard.generating' : undefined}/>
      )) || (
        <>
          {!filters && size(data) >= 1 ? <Divider variant="middle"/> : undefined}
          {(size(data) < 1 && (
            <Box
                flexGrow={1}
                display="flex"
                flexDirection="column"
                position="relative"
            >
              <CardContent className={filters ? `${content} ${emptyBox}` : content}>
                <Alert severity="info" variant="standard" className={emptyMsg}>
                  {generating ? undefined : (
                    <FormattedMessage
                        id={`supervisor.dashboard.${variant === TOP_CHART_SKILL_ADVISORS || variant === TOP_CHART_SKILL_COURSES
                          ? TOP_CHART_SKILLS_DEVELOPMENT : variant}.empty`}
                        values={values}
                    />
                  )}
                </Alert>
              </CardContent>
              {pending && data ? (
                <Box className={overlayDefault} position="relative">
                  <LinearProgress/>
                  {generating ? (
                    <BoxTypography
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexGrow={1}
                        height="90%"
                        fontWeight={fontWeightMedium}
                        color="text.secondary"
                    >
                      <FormattedMessage id="hr.dashboard.generating"/>
                    </BoxTypography>
                  ) : undefined}
                </Box>
              ) : undefined}
            </Box>
          )) || (
            <Box
                flexGrow={1}
                display="flex"
                flexDirection="column"
                position="relative"
            >
              {(variant === TOP_CHART_IN_DEMAND && (
                <TopChartStacked
                    variant="default"
                    hrbp={hrbp}
                    data={data as Skill[]}
                    totalEmployees={totalEmployees}
                    path={exportPending ? undefined : path}
                    pending={pending}
                />
              )) || (variant === TOP_CHART_SKILLS_GAP && hrbp && (
                <TopChartBars
                    data={data as (Job | MDJobsSkillsGap)[]}
                    totalEmployees={totalEmployees}
                    path={exportPending ? undefined : path}
                    pending={pending}
                />
              )) || (variant === TOP_CHART_EMPLOYEE_SKILLS && (
                <TopChartTimeline
                    variant="employees"
                    wide={wide}
                    data={data as EmployeeCountByDate[]}
                    years={optionalParams?.years}
                    pending={pending}
                />
              )) || ((
                variant === TOP_CHART_SKILLS_GROWTH ||
                variant === TOP_CHART_SUPPLY_DEMAND ||
                variant === TOP_CHART_SKILL_ADVISORS ||
                variant === TOP_CHART_SKILL_COURSES ||
                variant === TOP_CHART_SKILLS_DEVELOPMENT
              ) && (
                <TopChartStacked
                    variant={variant}
                    hrbp={hrbp}
                    data={data as (DASkillSupply | DASkillsDevelopment | DASkillsGrowth)[]}
                    totalEmployees={totalEmployees}
                    path={exportPending ? undefined : path}
                    pending={pending}
                />
              )) || (variant === TOP_CHART_JOB_MOVEMENT_SKILLS && (
                <TopChartStacked
                    variant={optionalParams?.upwardJobMovement ? 'upward_job_movement' : 'adj_job_movement'}
                    hrbp={hrbp}
                    data={data as DAJobMovementSkills[]}
                    totalEmployees={totalEmployees}
                    path={exportPending ? undefined : path}
                    pending={pending}
                />
              )) || (variant === TOP_CHART_JOBS_MATCH && (
                <TopChartTimeline
                    variant="jobs"
                    wide={wide}
                    data={data as DACloseMatchJobs[]}
                    years={optionalParams?.years}
                    pending={pending}
                />
              )) || (variant === TOP_CHART_AVG_SKILLS && (
                <TopChartTimeline
                    variant="skills"
                    wide={wide}
                    data={data as DAAverageSkills[]}
                    years={optionalParams?.years}
                    pending={pending}
                />
              )) || (variant === TOP_CHART_EMPLOYEE_VELOCITY && (
                <TopChartTimeline
                    variant="velocity"
                    wide={wide}
                    data={data as DAEmployeeVelocity[]}
                    years={optionalParams?.years}
                    pending={pending}
                />
              )) || (variant === TOP_CHART_CURRENT_ROLE_MATCH && (
                <TopChartTimeline
                    variant="match"
                    wide={wide}
                    data={data as DACurrentRoleMatch[]}
                    years={optionalParams?.years}
                    pending={pending}
                />
              )) || (variant === TOP_CHART_UPSKILLING && (
                <TopChartTimeline
                    variant="upskilling"
                    wide={wide}
                    data={data as DAUpskillingVelocity[]}
                    years={optionalParams?.years}
                    path={path}
                    pending={pending}
                />
              )) || ((variant === TOP_CHART_SKILL_COURSE_MAPPING || variant === TOP_CHART_COURSE_SKILL_MAPPING) && (
                <TopChartTimeline
                    variant={variant === TOP_CHART_SKILL_COURSE_MAPPING ? 'skill_course' : 'course_skill'}
                    wide={wide}
                    data={data as DASkillCourseMapping[]}
                    years={optionalParams?.years}
                    pending={pending}
                />
              )) || ((
                variant === TOP_CHART_JOBS_MOVEMENT ||
                variant === TOP_CHART_JOB_RELATED_SKILLS ||
                variant === TOP_CHART_HIRING_TRENDS
              ) && (
                <TopChartVBars
                    variant={variant}
                    data={data as (DAJobsMovement | DAJobRelatedSkills | DAHiringTrends)[]}
                    years={optionalParams?.years}
                    path={path}
                    pending={pending}
                />
              )) || (variant === TOP_CHART_FREQ_SKILL_GAP && (
                <TopChartStacked
                    variant="gaps"
                    hrbp={hrbp}
                    data={data as DASkillsInsights[]}
                    totalEmployees={totalEmployees}
                    path={exportPending ? undefined : path}
                    pending={pending}
                />
              )) || (variant === TOP_CHART_ORG_USAGE && (
                <TopChartRadar
                    data={data as DAOrgUsage[]}
                    path={path}
                    pending={pending}
                />
              )) || (
                <TopChartSimple
                    hrbp={hrbp}
                    data={data as (Skill | Job | MDTargetedJob | DAMostEffectiveCourses | DAMostEffectiveAdvisor)[]}
                    totalEmployees={totalEmployees}
                    path={exportPending ? undefined : path}
                    variant={
                      (variant === TOP_CHART_EFFECTIVE_COURSES && 'courses') ||
                      (variant === TOP_CHART_COURSE_COMPLETION && 'completion') ||
                      (variant === TOP_CHART_EFFECTIVE_ADVISORS && 'advisors') ||
                      undefined
                    }
                    pending={pending}
                />
              )}
              {pending && data ? (
                <Box className={overlayDefault} position="relative">
                  <LinearProgress/>
                  {generating ? (
                    <BoxTypography
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexGrow={1}
                        height="90%"
                        fontWeight={fontWeightMedium}
                        color="text.secondary"
                    >
                      <FormattedMessage id="hr.dashboard.generating"/>
                    </BoxTypography>
                  ) : undefined}
                </Box>
              ) : undefined}
            </Box>
          )}
        </>
      )}
    </>
  );

  return layout === 'full' ? innerContent : (
    <GridBox
        ref={ref}
        size={{
          xs: 12,
          lg: wide ? undefined : 6
        }}
        container
        alignItems="stretch"
        pt={2}
    >
      <Card elevation={2} className={card}>
        {innerContent}
      </Card>
    </GridBox>
  );
});

TopChart.displayName = 'TopChart';

export default memo(TopChart);
